"use client"

import { createContext, useContext, useState, useEffect } from "react"
import { getAuth } from "firebase/auth"
import { getDatabase, ref, set } from "firebase/database"

// Create the cart context
const CartContext = createContext()

// Custom hook to use the cart context
export const useCart = () => {
  return useContext(CartContext)
}

// Cart provider component
export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([])
  const [markedItems, setMarkedItems] = useState({})

  // Get auth and database
  const auth = getAuth()
  const database = getDatabase()

  // Load cart from localStorage on initial render
  useEffect(() => {
    const storedCart = localStorage.getItem("bakeryCart")
    if (storedCart) {
      try {
        setCartItems(JSON.parse(storedCart))
      } catch (error) {
        console.error("Error parsing stored cart:", error)
        setCartItems([])
      }
    }

    // Load marked items
    const storedMarkedItems = localStorage.getItem("markedItems")
    if (storedMarkedItems) {
      try {
        setMarkedItems(JSON.parse(storedMarkedItems))
      } catch (error) {
        console.error("Error parsing marked items:", error)
        setMarkedItems({})
      }
    }
  }, [])

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("bakeryCart", JSON.stringify(cartItems))
  }, [cartItems])

  // Save marked items to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("markedItems", JSON.stringify(markedItems))
  }, [markedItems])

  // Add item to cart
  const addToCart = (product, quantity = 1) => {
    setCartItems((prevItems) => {
      // Check if item already exists in cart
      const existingItemIndex = prevItems.findIndex((item) => item.id === product.id)

      if (existingItemIndex >= 0) {
        // Item exists, update quantity
        const updatedItems = [...prevItems]
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + quantity,
        }
        return updatedItems
      } else {
        // Item doesn't exist, add new item
        return [...prevItems, { ...product, quantity }]
      }
    })

    // Mark item as added
    setMarkedItems((prev) => ({
      ...prev,
      [product.id]: true,
    }))
  }

  // Update item quantity
  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return

    setCartItems((prevItems) => prevItems.map((item) => (item.id === id ? { ...item, quantity: newQuantity } : item)))
  }

  // Remove item from cart
  const removeItem = (id) => {
    setCartItems((prevItems) => prevItems.filter((item) => item.id !== id))

    // Unmark item
    setMarkedItems((prev) => {
      const updated = { ...prev }
      delete updated[id]
      return updated
    })
  }

  // Clear cart
  const clearCart = () => {
    setCartItems([])
    setMarkedItems({})
  }

  // Calculate cart totals
  const getCartTotals = () => {
    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
    const tax = subtotal * 0.0825 // 8.25% tax rate
    const shipping = cartItems.length > 0 ? 5.99 : 0
    const total = subtotal + tax + shipping

    return {
      subtotal,
      tax,
      shipping,
      total,
      itemCount: cartItems.reduce((count, item) => count + item.quantity, 0),
    }
  }

  // Check if item is marked (added to cart)
  const isItemMarked = (id) => {
    return !!markedItems[id]
  }

  // Create order in Firebase
  const createOrder = async (address) => {
    if (!auth.currentUser) {
      console.error("User not authenticated")
      return null
    }

    try {
      // Generate order number
      const timestamp = new Date().getTime().toString().slice(-6)
      const random = Math.floor(Math.random() * 10000)
        .toString()
        .padStart(4, "0")
      const orderNumber = `CRM-${timestamp}-${random}`

      // Get cart totals
      const totals = getCartTotals()

      // Ensure we store complete item details
      const orderItems = cartItems.map((item) => ({
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image: item.image || "/placeholder.svg",
        category: item.category,
        type: item.type,
        description: item.description,
      }))

      // Create order data
      const orderData = {
        userId: auth.currentUser.uid,
        orderNumber,
        items: orderItems,
        address,
        totals,
        status: "processing",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }

      // Save order to Firebase Realtime Database
      const ordersRef = ref(database, `orders/${orderNumber}`)
      await set(ordersRef, orderData)

      // Add order to user's orders list
      const userOrdersRef = ref(database, `users/${auth.currentUser.uid}/orders/${orderNumber}`)
      await set(userOrdersRef, true)

      // Save order data to localStorage for confirmation page
      localStorage.setItem("orderData", JSON.stringify(orderData))

      // Clear cart after successful order
      clearCart()

      return orderNumber
    } catch (error) {
      console.error("Error creating order:", error)
      return null
    }
  }

  const value = {
    cartItems,
    addToCart,
    updateQuantity,
    removeItem,
    clearCart,
    getCartTotals,
    isItemMarked,
    createOrder,
  }

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

